import { FC, useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from 'src/Store'
import { mapActions } from 'src/actions/MapActions'
import { layerParametersStyle } from './LayerParameters.style'
import { RadioSubLayers, SubLayerDescriptor, SubLayers, SwitchSubLayer, SwitchSubLayers } from 'src/dataSources/layers/Layer'
import { Close, ArrowBackIos, ArrowForwardIos } from '@material-ui/icons'
import { Slider, Typography, Grid, Input, IconButton, RadioGroup, FormControlLabel, Radio, Switch, Mark, List, ListItemText } from '@material-ui/core'
import { LayerParams, LayerParamsObject, LayerType } from 'src/dataSources/layers/LayerRegistry'
import { getLayerParamsFromType } from 'src/reducers/MapReducer'
import { wikiActions } from 'src/actions/WikiActions'
import { HydroModelObject } from 'src/dataSources/geo/HydroModel'
import { Extent } from 'openlayers'
import { isHydroModelIntersectingMapExtent } from 'src/utils/MapLayerUtils'
import { WikiDocumentMeta } from 'src/dataSources/wiki/Wiki'

export interface LayerParametersProps {
  layerType?: LayerType
  opacitySliderMode: boolean
  closeHandler: () => void
}

const defaultProps: LayerParametersProps = {
  layerType: undefined,
  opacitySliderMode: false,
  closeHandler: (): void => { }
}

/**
 * LayerParameters React component.
 * The following properties are implemented:
 * layerType {LayerType}: Layer to show parameters for.
 * @param props React properties object.
 * @returns React view component.
 */
export const LayerParameters: FC<LayerParametersProps> = (props: LayerParametersProps) => {
  const classes = layerParametersStyle()
  const dispatch = useDispatch()
  const intl = useIntl()
  const layerParams = useSelector((state: RootState): LayerParams[] => state.mapState.layerParams)
  const mapExtent = useSelector((state: RootState): Extent => state.mapState.mapExtent)

  // Local state to store values from params
  const [opacityEnabled, setOpacityEnabled] = useState<boolean>(false)
  const [opacity, setOpacity] = useState<number>(0.5)
  const [depthEnabled, setDepthEnabled] = useState<boolean>(false)
  const [depth, setDepth] = useState<number>(0)
  const [levelEnabled, setLevelEnabled] = useState<boolean>(false)
  const [level, setLevel] = useState<number>(0)
  const [radioSublayersEnabled, setRadioSublayersEnabled] = useState<boolean>(false)
  const [radioSublayers, setRadioSublayers] = useState<RadioSubLayers>({ labelId: '', selected: { labelId: '', subLayer: '' }, sublayers: [] })
  const [switchSublayersEnabled, setSwitchSublayersEnabled] = useState<boolean>(false)
  const [switchSublayers, setSwitchSublayers] = useState<SwitchSubLayers>({ labelId: '', sublayers: [] })
  const [subLayersEnabled, setSubLayersEnabled] = useState<boolean>(false)
  const [subLayers, setSubLayers] = useState<SubLayers>({ labelId: '', key: '', list: [], subLayerDescriptors: [], minimum: 0, maximum: 0, step: 0, selected: { value: 0, subLayer: '' } })
  const wikiDocumentsMeta = useSelector((state: RootState): WikiDocumentMeta[] => state.wikiState.documentsMeta)

  const [subLayerDescriptors, setSubLayerDescriptors] = useState<SubLayerDescriptor[]>([] as SubLayerDescriptor[])

  const hydroModels: HydroModelObject[] = useSelector((state: RootState): HydroModelObject[] => state.hydroModelState.hydroModels)

  // Determine which params are available
  useEffect(() => {
    if (props.layerType === undefined) {
      return
    }
    const params: LayerParamsObject | undefined = getParameters()
    if (params === undefined) {
      return
    }
    if (props.opacitySliderMode) {
      if (params.opacity !== undefined) {
        setOpacityEnabled(true)
        setOpacity(params.opacity)
      }
      return
    }
    if (params.depth !== undefined) {
      setDepthEnabled(true)
      setDepth(params.depth)
    }
    if (params.level !== undefined) {
      setLevelEnabled(true)
      setLevel(params.level)
    }
    if (params.radioSubLayers !== undefined) {
      setRadioSublayersEnabled(true)
      setRadioSublayers(params.radioSubLayers)
    }
    if (params.switchSubLayers !== undefined) {
      setSwitchSublayersEnabled(true)
      setSwitchSublayers(params.switchSubLayers)
    }
    if (params.subLayers !== undefined) {
      setSubLayersEnabled(true)
      setSubLayers(params.subLayers)
      setSubLayerDescriptors(params.subLayers.subLayerDescriptors)
    }
    // eslint-disable-next-line
  }, [layerParams, props.layerType])

  /**
   * Get hydro model sub layer object.
   * @param hydroModel Hydro model to get sub layer format.
   * @param enabled Is true the hydro model enabled in the layer picker.
   */
  const getHydroModelSubLayer = (hydroModel: HydroModelObject, enabled: boolean): SwitchSubLayer | undefined => {
    if (hydroModel.WMSLayerName1 === undefined || hydroModel.WMSLayerName1 === '') {
      return undefined
    }
    return {
      id: `${hydroModel.Id}`,
      title: hydroModel.Name + (hydroModel.MessuringPeriode !== null && hydroModel.MessuringPeriode !== '' ? ' (' + hydroModel.MessuringPeriode + ')' : ''),
      subLayer: hydroModel.WMSLayerName1,
      enabled: enabled
    }
  }

  /**
   * Get parameters object from layer params string.
   * @returns LayerParamsObject
   */
  const getParameters = (): LayerParamsObject | undefined => {
    if (props.layerType !== undefined) {
      return getLayerParamsFromType(props.layerType, layerParams)
    }
  }

  /**
   * Gets a SubLayerDescriptor from a value
   * @param value The value for which to get the SubLayerDescriptor
   * @returns A SubLayerDescriptor if such exists for a given subLayerType and value
   */
  const getSubLayerDescriptor = (value: number): SubLayerDescriptor | undefined => {
    return subLayerDescriptors.find(descriptor => descriptor.value === value)
  }

  const isInputValid = (value: number, min: number, max: number, step?: number): boolean => {
    if (value < min || value > max) {
      return false
    }

    if (step === undefined) {
      return true
    }

    const remainder = ((((value - min) * 1000) % (step * 1000)) / 1000)
    return remainder === 0
  }

  /**
   * Update a given parameter with a new value in state.
   * @param param Parameter name.
   * @param value New parameter value.
   * @param commit If true commit to state.
   */
  const updateParameter = (param: string, value: number, commit: boolean): void => {
    const params = getParameters()
    if (params === undefined) {
      return
    }
    if (props.layerType === undefined) {
      return
    }
    switch (param) {
      case 'opacity':
        params.opacity = value / 100
        setOpacity(params.opacity)
        break
      case 'level':
        params.level = value
        setLevel(params.level)
        break
      case 'depth':
        params.depth = value
        setDepth(params.depth)
        break
      case 'subLayer': {
        if (params.subLayers === undefined) {
          return
        }
        const subLayerDescriptor = getSubLayerDescriptor(value)
        if (subLayerDescriptor !== undefined) {
          params.subLayers.selected = subLayerDescriptor
          setSubLayers({
            ...subLayers,
            selected: subLayerDescriptor
          })
        }
        break
      }
      default:
        break
    }
    if (commit) {
      dispatch(mapActions.setLayerParams({
        layerType: props.layerType,
        params: params
      }))
    }
  }

  /**
   * Handles the update of a switch state by updating its parameter.
   * @param param Parameter name.
   * @param id Id to toggle.
   * @param checked Checked state of control.
   */
  const handleSwitchChange = (param: string, id: string, checked: boolean): void => {
    const params = getParameters()
    if (params === undefined) {
      return
    }
    if (props.layerType === undefined) {
      return
    }
    switch (param) {
      case 'switchSublayer': {
        if (params.switchSubLayers === undefined) {
          return
        }
        if (props.layerType === LayerType.HYDRO_MODEL) {
          params.switchSubLayers = switchSublayers
        }
        params.switchSubLayers.sublayers.forEach(subLayer => {
          if (id === subLayer.subLayer) {
            subLayer.enabled = checked
          }
        })
        setSwitchSublayers(params.switchSubLayers)
        break
      }
      default:
        break
    }

    dispatch(mapActions.setLayerParams({
      layerType: props.layerType,
      params: params
    }))
  }

  /**
   * Handles the update of a radio button state by updating its parameter.
   * @param param Parameter name.
   * @param value New parameter value.
   */
  const handleRadioChange = (param: string, value: string): void => {
    const params = getParameters()
    if (params === undefined) {
      return
    }
    if (props.layerType === undefined) {
      return
    }
    switch (param) {
      case 'radioSublayer': {
        if (params.radioSubLayers === undefined) {
          return
        }
        const selectedSubLayer = params.radioSubLayers.sublayers.find(l => l.subLayer === value)
        if (selectedSubLayer === undefined) {
          return
        }
        params.radioSubLayers.selected = selectedSubLayer
        setRadioSublayers({
          ...radioSublayers,
          selected: selectedSubLayer
        })
        break
      }
      default:
        break
    }

    dispatch(mapActions.setLayerParams({
      layerType: props.layerType,
      params: params
    }))
  }

  /**
   * Handle value changed in slider.
   * @param param Slide parameter name.
   * @param value Slide value.
   */
  const handleSliderChange = (param: string, value: number | number[], commit: boolean): void => {
    updateParameter(param, value as number, commit)
  }

  /**
   * Handle changing of input value.
   * @param event React event object.
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, min: number, max: number, step?: number): void => {
    const value = Number(event.target.value)
    const validInput = isInputValid(value, min, max, step)

    if (validInput) {
      updateParameter(event.target.name, value, true)
    }
  }

  /**
   * Handle when close button is clicked.
   */
  const handleCloseButtonClick = (): void => {
    props.closeHandler()
  }

  /**
   * Handle when a sub layer label is clicked.
   * @param subLayer Sub layer that was clicked.
   */
  const handleSubLayerLabelClicked = (subLayer: SubLayerDescriptor): void => {
    // If wiki key is defined open document
    const wikiKey = subLayer.wikiKey
    if (wikiKey !== undefined && hasDocument(wikiKey)) {
      dispatch(wikiActions.fetchDocument(wikiKey))
    }
  }

  /**
   * Checks if a wiki document with the specified key exists
   * @param wikiKey key identifying the wiki
   * @returns return true if a ducument exists
   */
  const hasDocument = (wikiKey: string|undefined): boolean => {
    return wikiKey === undefined ? false : wikiDocumentsMeta.some(meta => meta.key === wikiKey)
  }

  /**
   * Gets a list of Marks from a list of SubLayerDescriptor
   * @returns A list of Marks
   */
  const getMarks = (descriptors: SubLayerDescriptor[]): Mark[] => {
    return descriptors.map(descriptor => {
      return {
        value: descriptor.value
      }
    })
  }

  /**
   * Determines if a SubLayer with a lower value than the currently selected SubLayer exists
   * @returns Returns true if SubLayer exists or false otherwise
   */
  const hasPrevSubLayer = (): boolean => {
    return subLayers.selected.value > subLayers.minimum
  }

  /**
   * Determines if a SubLayer with a larger value than the currently selected SubLayer exists
   * @returns Returns true if SubLayer exists or false otherwise
   */
  const hasNextSubLayer = (): boolean => {
    return subLayers.selected.value < subLayers.maximum
  }

  /**
   * Updates the selected SubLayer to be the first SubLayer, in a sorted list, that has a lower value then the currently selected SubLayer, if such exists.
   */
  const selectPrevSubLayer = (): void => {
    const descriptors: SubLayerDescriptor[] = Object.assign([], subLayerDescriptors)
    const value = descriptors
      .sort((a, b) => {
        return b.value - a.value
      })
      .find(descriptor => descriptor.value < subLayers.selected.value)
      ?.value
    if (value !== undefined) {
      handleSliderChange('subLayer', value, true)
    }
  }

  /**
   * Updates the selected SubLayer to be the first SubLayer, in a sorted list, that has a larger value then the currently selected SubLayer, if such exists.
   */
  const selectNextSubLayer = (): void => {
    const descriptors: SubLayerDescriptor[] = Object.assign([], subLayerDescriptors)
    const value = descriptors
      .sort((a, b) => {
        return a.value - b.value
      })
      .find(descriptor => descriptor.value > subLayers.selected.value)
      ?.value
    if (value !== undefined) {
      handleSliderChange('subLayer', value, true)
    }
  }

  // Filter hydro models based on map extent
  useEffect(() => {
    if (props.layerType !== LayerType.HYDRO_MODEL) {
      return
    }
    if (hydroModels === undefined) {
      return
    }
    if (mapExtent === undefined) {
      return
    }

    const params = getParameters()
    const hydroModelSubLayers: SwitchSubLayers = { labelId: 'layerpicker.switchsublayer', sublayers: [] }
    for (const hydroModel of hydroModels) {
      const enabled = params?.switchSubLayers !== undefined ? params.switchSubLayers.sublayers.some(subLayer => subLayer.subLayer === hydroModel.WMSLayerName1 && subLayer.enabled) : false

      // Test if hydro model bounding box intersect with current map extent
      if (isHydroModelIntersectingMapExtent(mapExtent, hydroModel) || enabled) {
        const hydroModelSubLayer = getHydroModelSubLayer(hydroModel, enabled)
        if (hydroModelSubLayer === undefined) {
          continue
        }
        hydroModelSubLayers.sublayers.push(hydroModelSubLayer)
      }
    }

    // Update local state
    setSwitchSublayers(hydroModelSubLayers)

    // eslint-disable-next-line
  }, [hydroModels, mapExtent, layerParams, props.layerType])  

  return (
    <div className={classes.root}>
      <div className={classes.closeButton}>
        <IconButton size='medium' className={classes.closeButtonIcon} onClick={handleCloseButtonClick}>
          <Close className={classes.closeButtonIcon} />
        </IconButton>
      </div>

      {opacityEnabled && (
        <>
          <Typography id='discrete-slider-opacity' className={classes.discreteSlider} gutterBottom>
            <FormattedMessage id='layerpicker.opacity' />
          </Typography>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={9}>
              <Slider
                name='opacity'
                defaultValue={50}
                aria-labelledby='discrete-slider-opacity'
                valueLabelDisplay='off'
                min={0}
                max={100}
                value={Math.round(opacity * 100)}
                onChange={(event: any, newValue: number | number[]): void => { handleSliderChange('opacity', newValue, false) }}
                onChangeCommitted={(event: any, newValue: number | number[]): void => { handleSliderChange('opacity', newValue, true) }}
                className={classes.slider}
              />
            </Grid>
            <Grid item xs={3} className={classes.inputWrapper}>
              <Input
                name='opacity'
                className={`${classes.input} ${(classes.normal)}`}
                value={Math.round(opacity * 100)}
                margin='dense'
                disableUnderline
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => { handleInputChange(event, 0, 100) }}
                inputProps={{
                  style: { textAlign: 'center', padding: 0 },
                  step: 10,
                  min: 0,
                  max: 100,
                  type: 'number',
                  'aria-labelledby': 'input-slider'
                }}
              /> %
            </Grid>
          </Grid>
        </>
      )}
      {levelEnabled && (
        <>
          <Typography id='discrete-slider-level' className={classes.discreteSlider} gutterBottom>
            <FormattedMessage id='layerpicker.level' />
          </Typography>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs>
              <Slider
                name='level'
                defaultValue={-10}
                aria-labelledby='discrete-slider-level'
                valueLabelDisplay='off'
                min={-100}
                max={100}
                value={level}
                onChange={(event: any, newValue: number | number[]): void => { handleSliderChange('level', newValue, false) }}
                onChangeCommitted={(event: any, newValue: number | number[]): void => { handleSliderChange('level', newValue, true) }}
                className={classes.slider}
              />
            </Grid>
            <Grid item>
              <Input
                name='level'
                className={`${classes.input} ${(classes.normal)}`}
                value={level}
                margin='dense'
                disableUnderline
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => { handleInputChange(event, -100, 100) }}
                inputProps={{
                  style: { textAlign: 'center', padding: 0 },
                  step: 10,
                  min: -100,
                  max: 100,
                  type: 'number',
                  'aria-labelledby': 'input-slider'
                }}
              /> m
            </Grid>
          </Grid>
        </>
      )}
      {depthEnabled && (
        <>
          <Typography id='discrete-slider-depth' className={classes.discreteSlider} gutterBottom>
            <FormattedMessage id='layerpicker.depth' />
          </Typography>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs>
              <Slider
                name='depth'
                defaultValue={10}
                aria-labelledby='discrete-slider-depth'
                valueLabelDisplay='off'
                min={-100}
                max={100}
                value={depth}
                onChange={(event: any, newValue: number | number[]): void => { handleSliderChange('depth', newValue, false) }}
                onChangeCommitted={(event: any, newValue: number | number[]): void => { handleSliderChange('depth', newValue, true) }}
                className={classes.slider}
              />
            </Grid>
            <Grid item>
              <Input
                name='depth'
                className={`${classes.input} ${(classes.normal)}`}
                value={depth}
                margin='dense'
                disableUnderline
                onChange={(event: React.ChangeEvent<HTMLInputElement>): void => { handleInputChange(event, -100, 100) }}
                inputProps={{
                  style: { textAlign: 'center', padding: 0 },
                  step: 10,
                  min: -100,
                  max: 100,
                  type: 'number',
                  'aria-labelledby': 'input-slider'
                }}
              /> m
            </Grid>
          </Grid>
        </>
      )}
      {radioSublayersEnabled && (
        <>
          <Typography id='discrete-slider-level' className={classes.discreteRadio} gutterBottom>
            <FormattedMessage id={radioSublayers.labelId} />
          </Typography>
          <Grid container spacing={2} alignItems='center'>
            <RadioGroup
              aria-label='discrete-radio-sublayers'
              name={intl.formatMessage({ id: radioSublayers.labelId })}
              value={radioSublayers.selected.subLayer} onChange={(event) => { handleRadioChange('radioSublayer', event.target.value) }}
              className={classes.radioGroup}
              defaultValue={radioSublayers.selected.subLayer}
            >
              {
                radioSublayers.sublayers.map((subLayer, index) => {
                  return (
                    <FormControlLabel
                      key={index}
                      value={subLayer.subLayer}
                      control={<Radio color='primary' />}
                      label={intl.formatMessage({ id: subLayer.labelId })}
                      className={classes.radioLabel}
                    />
                  )
                })
              }
            </RadioGroup>
          </Grid>
        </>
      )}
      {switchSublayersEnabled && (
        <>
          <Typography id='discrete-switch-sub-layers' className={classes.discreteSwitch} gutterBottom>
            <FormattedMessage id={switchSublayers.labelId} />
          </Typography>
          <Grid container spacing={2} alignItems='center'>
            {
              switchSublayers.sublayers.map((subLayer, index) => {
                return (
                  <FormControlLabel
                    key={index}
                    control={
                      <Switch
                        checked={subLayer.enabled}
                        onChange={event => handleSwitchChange('switchSublayer', event.target.name, event.target.checked)}
                        name={subLayer.subLayer}
                        color='primary'
                      />
                    }
                    label={subLayer.labelId !== undefined ? intl.formatMessage({ id: subLayer.labelId }) : (subLayer.title !== undefined ? subLayer.title : '')}
                    className={classes.switchLabel}
                  />
                )
              })
            }
            {switchSublayers.sublayers.length === 0 && (
              <div className={classes.message}>
                <FormattedMessage id='layerpicker.nomaplayersavailable' />
              </div>
            )}
          </Grid>
        </>
      )}
      {subLayersEnabled && (
        <>
          <Typography id='discrete-slider-sub-layers' className={classes.discreteSlider} gutterBottom>
            {intl.formatMessage({ id: subLayers.labelId })}
          </Typography>
          <Grid container spacing={subLayers.stepperEnabled === true ? 0 : 2} alignItems='center'>
            <Grid item xs={subLayers.stepperEnabled === true ? 12 : 9}>
              <Slider
                name='subLayer'
                defaultValue={subLayers.minimum}
                aria-labelledby='discrete-slider-sub-layers'
                valueLabelDisplay='off'
                min={subLayers.minimum}
                max={subLayers.maximum}
                step={null}
                marks={getMarks(subLayerDescriptors)} // getMarks takes subLayerDescriptors as input to make the slider update on state changes
                value={subLayers.selected.value}
                onChange={(event: any, newValue: number | number[]): void => {
                  handleSliderChange('subLayer', newValue, false)
                }}
                onChangeCommitted={(event: any, newValue: number | number[]): void => {
                  handleSliderChange('subLayer', newValue, true)
                }}
                className={classes.marksSlider}
              />
            </Grid>
            {subLayers.stepperEnabled === true && (
              <Grid item xs={12}>
                <Grid container alignItems='center'>
                  <Grid item xs={2} className={classes.valueStepDownButton}>
                    <>
                      {hasPrevSubLayer() &&
                        <IconButton size='medium' className={classes.arrowButtonIcon} onClick={() => selectPrevSubLayer()}>
                          <ArrowBackIos className={classes.arrowButtonIcon} />
                        </IconButton>}
                    </>
                  </Grid>
                  <Grid item xs={8}>
                    <List disablePadding>
                      <ListItemText>
                        <div className={classes.sliderValueText}>
                          {subLayers.selected.label}
                        </div>
                      </ListItemText>
                      {subLayers.selected.subLabel !== undefined &&
                        <ListItemText>
                          <div
                            className={hasDocument(subLayers.selected.wikiKey) ? classes.sliderSubLabelTextWithWikiDocument : classes.sliderSubLabelText}
                            onClick={() => handleSubLayerLabelClicked(subLayers.selected)}
                          >
                            {intl.formatMessage({ id: subLayers.selected.subLabel })}
                          </div>
                        </ListItemText>}
                    </List>
                  </Grid>
                  <Grid item xs={2}>
                    <>
                      {hasNextSubLayer() &&
                        <IconButton size='medium' className={classes.arrowButtonIcon} onClick={() => selectNextSubLayer()}>
                          <ArrowForwardIos className={classes.arrowButtonIcon} />
                        </IconButton>}
                    </>
                  </Grid>
                </Grid>
              </Grid>)}
            {subLayers.stepperEnabled !== true && (
              <Grid item xs={3}>
                <Grid container className={classes.valueWrapper}>
                  <Grid item xs alignItems='center'>
                    <div className={classes.sliderValueText}>
                      {subLayers.selected.value}
                    </div>
                  </Grid>
                  <Grid item>
                    {subLayers.unit}
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        </>
      )}
    </div>
  )
}

LayerParameters.defaultProps = defaultProps

export default LayerParameters
